// deps
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "styled-components";

// components
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import {
    TextFieldWithChips,
    IChipItem,
} from "@swiggy-private/dls-web-recipe-common-web/dist/components/TextFieldWithChips";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";

// utils
import { LottieAnimation, useEffectOnce } from "@swiggy-private/core-ui";

// styles
import {
    UpiStepWrapper,
    LottieContainer,
    ContentSection,
    UpiStepHeading,
    UpiTextFieldSection,
    DisabledChipsOverlay,
} from "./upiStepStyles";
import { ButtonContainer, ToastContainer } from "./styled";

// constants
import {
    SPLIT_BILL_LOTTIE_PROPS,
    UPI_STEP_HEADING,
    UPI_ID_LABEL,
    UPI_HINT_TEXT,
    SEND_REQUEST_BUTTON_TEXT,
    UPI_ID_FIELD_NAME,
    UPI_LOTTIE_PATH,
    UPI_ID_REGEX,
    UPI_ID_REQUIRED_ERROR,
    UPI_ID_INVALID_ERROR,
    UPI_SUFFIX_CHIPS,
    UPI_USERNAME_REQUIRED_TOAST,
} from "@dineout-payment-success-ui/constants";

// types
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";

// hooks
import { useSplitBillAnalytics } from "@dineout-payment-success-ui/hooks/useSplitBillAnalytics";

// utils
import { getCloudinaryRawUrl, isEmpty } from "@swiggy-private/js-utils";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { getUpiPrefix, getChipFilterQuery, UPI_ID_SEPARATOR } from "./splitBillHelpers";

interface SplitBillUpiStepProps {
    onSendRequest: (upiId: string) => void;
    orderId?: string;
    totalBillAmount?: number;
    splitMethod?: string;
    restaurantId?: string;
}

const shouldShowChip = (chipLabel: string, filterQuery: string | null): boolean => {
    if (filterQuery === null || isEmpty(filterQuery)) {
        return true;
    }

    return chipLabel.toLowerCase().includes(filterQuery);
};

const hasMultipleUpiSeparators = (value: string): boolean => {
    const firstAtIndex = value.indexOf(UPI_ID_SEPARATOR);
    return firstAtIndex !== -1 && value.indexOf(UPI_ID_SEPARATOR, firstAtIndex + 1) !== -1;
};

const getChipsWithState = (value: string): IChipItem[] => {
    if (hasMultipleUpiSeparators(value)) {
        return [];
    }

    const isFieldEmpty = isEmpty(value.trim());
    const filterQuery = getChipFilterQuery(value);

    return UPI_SUFFIX_CHIPS.filter((chip) => shouldShowChip(chip.label, filterQuery)).map((chip) => ({
        ...chip,
        isDisabled: isFieldEmpty,
        isSelected: !isFieldEmpty && value.toLowerCase().endsWith(chip.label.toLowerCase()),
    }));
};

const SplitBillUpiStep: React.FC<SplitBillUpiStepProps> = ({
    onSendRequest,
    orderId = "",
    totalBillAmount = 0,
    splitMethod = "",
    restaurantId = "",
}) => {
    const theme = useTheme();
    const [upiId, setUpiId] = useState("");
    const [error, setError] = useState("");
    const [chipToastMessage, setChipToastMessage] = useState("");
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    useEffect(() => {
        const prev = window.keyboardStateChanged;
        window.keyboardStateChanged = (visible: boolean, keyboardHeight: number): void => {
            setIsKeyboardVisible(visible);
            prev?.(visible, keyboardHeight);
        };
        return (): void => {
            window.keyboardStateChanged = prev;
        };
    }, []);

    const isFieldEmpty = isEmpty(upiId.trim());

    // Analytics hook
    const { onClickSplitUpiEntry, onImpressionSplitUpiHandle, onClickSplitUpiHandle } = useSplitBillAnalytics();

    const validateUpiId = useCallback((value: string): boolean => {
        const trimmed = value.trim();
        if (isEmpty(trimmed)) {
            setError(UPI_ID_REQUIRED_ERROR);
            return false;
        }
        if (!UPI_ID_REGEX.test(trimmed)) {
            setError(UPI_ID_INVALID_ERROR);
            return false;
        }
        setError("");
        return true;
    }, []);

    const chips = useMemo(() => getChipsWithState(upiId), [upiId]);

    useEffectOnce(() => {
        onImpressionSplitUpiHandle({
            suggestedUpiHandles: chips.map((chip) => chip.label),
        });
    });

    const handleUpiIdChange = (value: string) => {
        setUpiId(value);
        if (error) {
            setError("");
        }
    };

    const handleDisabledChipClick = useCallback(() => {
        setChipToastMessage(UPI_USERNAME_REQUIRED_TOAST);
    }, []);

    const handleChipToastDismiss = useCallback(() => {
        setChipToastMessage("");
    }, []);

    const handleChipClick = useCallback(
        (chip: IChipItem) => {
            const chipValue = chip.value ?? chip.label;
            const newValue = getUpiPrefix(upiId) + chipValue;
            setUpiId(newValue);
            if (error) {
                setError("");
            }

            onClickSplitUpiHandle({
                selectedUpiHandle: chip.label,
                suggestedUpiHandles: chips.map((c) => c.label),
                position: chips.findIndex((c) => c.label === chip.label),
            });
        },
        [upiId, error, chips, onClickSplitUpiHandle],
    );

    const handleSendRequest = useCallback(() => {
        // Fire UPI entry click analytics
        onClickSplitUpiEntry({
            bookingId: orderId,
            totalBillAmount,
            splitMethod,
            upiSelected: upiId ? 1 : 0,
            restaurantId,
        });

        if (validateUpiId(upiId)) {
            onSendRequest(upiId.trim());
        }
    }, [
        onClickSplitUpiEntry,
        orderId,
        totalBillAmount,
        splitMethod,
        upiId,
        restaurantId,
        validateUpiId,
        onSendRequest,
    ]);

    return (
        <UpiStepWrapper data-testid={SPLIT_BILL_TEST_IDS.UPI_STEP}>
            {!isEmpty(chipToastMessage) && (
                <ToastContainer>
                    <Toast
                        message={chipToastMessage}
                        background="background_Primary"
                        show
                        autoDismiss
                        onDismiss={handleChipToastDismiss}
                        containerStyle={{
                            width: "100%",
                        }}
                        leftAsset={<SpriteIconWeb iconName="infoFilled16" fillColor="text_Highest_Emphasis" />}
                    />
                </ToastContainer>
            )}

            {!isKeyboardVisible && (
                <LottieContainer>
                    <LottieAnimation
                        fileName={getCloudinaryRawUrl(UPI_LOTTIE_PATH)}
                        visible={true}
                        playLottie
                        {...SPLIT_BILL_LOTTIE_PROPS}
                    />
                </LottieContainer>
            )}

            <ContentSection>
                <UpiStepHeading variant="V2.subheading1.neutral.bold" color="text_High_Emphasis">
                    {UPI_STEP_HEADING}
                </UpiStepHeading>

                <UpiTextFieldSection>
                    <TextFieldWithChips
                        textFieldHorizontalPadding="medium"
                        textFieldDetails={{
                            value: upiId,
                            onChange: handleUpiIdChange,
                            type: TextFieldTypes.TEXT,
                            name: UPI_ID_FIELD_NAME,
                            label: UPI_ID_LABEL,
                            hintText: error ? "" : UPI_HINT_TEXT,
                            errorText: error,
                            inputStyle: {
                                backgroundColor: theme.colors[theme.colorMode].background_Primary,
                            },
                            attrs: {
                                required: true,
                                "aria-required": true,
                                "data-testid": SPLIT_BILL_TEST_IDS.UPI_INPUT,
                            },
                        }}
                        chips={chips}
                        onChipClick={handleChipClick}
                    />
                    {isFieldEmpty && <DisabledChipsOverlay onClick={handleDisabledChipClick} aria-hidden="true" />}
                </UpiTextFieldSection>
            </ContentSection>

            <ButtonContainer>
                <FullWidthButton
                    variant="primary"
                    type="button"
                    onClick={handleSendRequest}
                    data-testid={SPLIT_BILL_TEST_IDS.SEND_REQUEST}
                >
                    {SEND_REQUEST_BUTTON_TEXT}
                </FullWidthButton>
            </ButtonContainer>
        </UpiStepWrapper>
    );
};

export default memo(SplitBillUpiStep);
